import { legacy_createStore as createStore, combineReducers, applyMiddleware, Reducer } from "redux";
import {thunk}  from 'redux-thunk';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import userReducer from "../reducers/userReducer";
import notificationReducer from "../reducers/notificationReducer";
import storage from "redux-persist/lib/storage";
import authReducer from "../reducers/authReducer";
import { LOGOUT } from "../actions/userActionCreator";

const appReducer = combineReducers({
    user: userReducer,
    notifications: notificationReducer,
    auth: authReducer
});

// Export RootState type for type-safe selectors
export type RootState = ReturnType<typeof appReducer>;

// LOGOUT resets every slice to initial state so no personal data survives
// into the next session. locale/rtl are preserved so Arabic users don't
// snap to LTR on the sign-in screen after signing out.
const rootReducer: Reducer = (state: RootState | undefined, action: any) => {
    if (action.type === LOGOUT && state) {
        const locale = state.user?.locale;
        const rtl = state.user?.rtl;
        const reset = appReducer(undefined, action) as RootState;
        return { ...reset, user: { ...reset.user, locale, rtl } };
    }
    return appReducer(state, action);
};

// Transient boot flags must NEVER survive a page load.
//
// `loading`, `firstLoad` and `bootError` describe *this* page load, not the
// session. The whole user slice is persisted with no filter, so a boot that
// failed halfway — isLoggedIn true, loading false, role "", userId null — was
// written to localStorage verbatim and faithfully restored on every subsequent
// cold load. That is what turned a one-shot GetUserInfo failure into a
// permanent one: the app re-entered the broken shell instead of re-booting.
//
// A TRANSFORM rather than a nested persistReducer on purpose: a nested config
// moves the slice into its own `persist:user` storage key, and both
// `utils/request.ts` (token fallback #3) and `i18n.js` (initial language) read
// the user slice out of `persist:root` by hand. Filtering in place keeps that
// layout byte-for-byte and changes only which fields are written.
//
// The OUTBOUND half matters as much as the inbound one: browsers that already
// hold a poisoned `persist:root` from before this fix must not keep replaying
// `loading: false` on top of an empty user.
//
// `localeUserSet` rides along for the same reason, with the opposite intent.
// It marks "the user picked a language in THIS page session", so a late boot
// `GetUserInfo` cannot revert the choice mid-session. Stripping it on write means
// a NEW page load starts with the marker clear and the server's stored locale
// wins again — which is precisely what LanguageSwitch's toast promises the user
// ("It will apply for this session."). It is a transient session flag, not state.
const transientBootFlagsTransform = createTransform(
    // inbound — on write: drop the flags entirely.
    (inboundState: any) => {
        if (!inboundState || typeof inboundState !== 'object') return inboundState;
        const rest: any = { ...inboundState };
        delete rest.loading;
        delete rest.firstLoad;
        delete rest.bootError;
        delete rest.localeUserSet;
        return rest;
    },
    // outbound — on read: force the boot defaults regardless of what is stored.
    (outboundState: any) => {
        if (!outboundState || typeof outboundState !== 'object') return outboundState;
        return { ...outboundState, loading: true, firstLoad: true, bootError: false, localeUserSet: false };
    },
    { whitelist: ['user'] }
);

const persistConfig = {
    key: 'root',
    storage,
    transforms: [transientBootFlagsTransform],
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

let storeInstance: ReturnType<typeof createStore<typeof persistedReducer>> | null = null;
let persistorInstance: ReturnType<typeof persistStore> | null = null;

/**
 * Returns the singleton Redux store + persistor.
 * IMPORTANT: Never call createStore more than once in the app runtime.
 */
export default function configureStore() {
    if (!storeInstance) {
        storeInstance = createStore(persistedReducer, applyMiddleware(thunk));
        persistorInstance = persistStore(storeInstance);
    }
    return { store: storeInstance, persistor: persistorInstance };
}

export const getStore = () => configureStore().store;
export const getPersistor = () => configureStore().persistor;